import React, { useEffect, useMemo, useState } from "react";
import CustomSelect from "@/shared/components/ui/Select/CustomSelect";
import CustomCommonTooltip from "@/shared/components/ui/Upload/ToolTip/CustomCommonTooltip";
import InlineAlert from "@/shared/components/ui/Alert/InlineAlert";
import {
  getMessageSeeds,
  getTemplateVariableValues,
  getVariablesWithoutFallback,
  renderMessageSegments,
} from "@/features/campaigns/utils/messageTemplate";
import { getComposerCopy, TEMPLATE_VARIABLES } from "@/features/campaigns/utils/stepContentMode";

const VARIABLE_LABELS = Object.fromEntries(TEMPLATE_VARIABLES.map((v) => [v.key, v.label.toLowerCase()]));

const Segments = ({ segments }) =>
  segments.map((segment, index) => {
    const key = `${segment.type}-${index}`;
    if (segment.type === "text") return <React.Fragment key={key}>{segment.text}</React.Fragment>;
    if (segment.type === "unknown") {
      return (
        <span key={key} className="rounded bg-error-50 px-0.5 text-error-600">
          {segment.text}
        </span>
      );
    }
    const label = VARIABLE_LABELS[segment.name] ?? segment.name;
    if (segment.status === "filled") return <React.Fragment key={key}>{segment.text}</React.Fragment>;
    if (segment.status === "fallback") {
      return (
        <CustomCommonTooltip key={key} content={`This lead has no ${label}, so your fallback is used.`}>
          <span className="underline decoration-dotted decoration-secondary underline-offset-2">{segment.text}</span>
        </CustomCommonTooltip>
      );
    }
    return (
      <CustomCommonTooltip key={key} content={`This lead has no ${label}; it will be left blank. Add a fallback.`}>
        <span className="rounded bg-warning-50 px-1 text-warning-700 dark:bg-amber-950/60 dark:text-amber-300">
          [{label}]
        </span>
      </CustomCommonTooltip>
    );
  });

/** Renders the message exactly as a chosen lead receives it, and flags values leads lack. */
const MessagePreview = ({ channel, subject = "", body = "", leads = [], sender }) => {
  const [leadId, setLeadId] = useState(leads[0]?.id);
  useEffect(() => {
    if (!leads.some((lead) => lead.id === leadId)) setLeadId(leads[0]?.id);
  }, [leads, leadId]);

  const showSubject = getComposerCopy(channel).hasSubject;
  const lead = leads.find((item) => item.id === leadId) ?? leads[0];
  const values = useMemo(() => getTemplateVariableValues(lead, sender), [lead, sender]);
  const seeds = getMessageSeeds(lead);
  const subjectSegments = renderMessageSegments(subject, { values, seed: seeds.subject });
  const bodySegments = renderMessageSegments(body, { values, seed: seeds.body });

  const gaps = useMemo(() => {
    const names = getVariablesWithoutFallback(`${showSubject ? subject : ""}\n${body}`);
    return names
      .map((name) => ({
        name,
        count: leads.filter((item) => !getTemplateVariableValues(item, sender)[name]).length,
      }))
      .filter((gap) => gap.count > 0);
  }, [showSubject, subject, body, leads, sender]);

  const hasContent = Boolean(body.trim() || (showSubject && subject.trim()));

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-900/40 p-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold text-gray-800 dark:text-gray-100">Preview</p>
        <CustomSelect
          options={leads.map((item) => ({
            key: item.id,
            title: item.isSample ? (item.full_name ? `Sample: ${item.full_name}` : "Sample: lead missing details") : item.full_name || "Unnamed lead",
            description: item.company || undefined,
          }))}
          selectedKeys={new Set(lead ? [lead.id] : [])}
          onSelectionChange={(keys) => setLeadId([...keys][0])}
          placeholder="Choose a lead"
          width="w-60"
          size="sm"
          showDescription
          aria-label="Preview as lead"
        />
      </div>
      {!hasContent ? (
        <p className="text-xs text-gray-400">Your message preview appears here.</p>
      ) : (
        <div className="flex flex-col gap-2 rounded-lg bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 p-3 text-xs text-gray-800 dark:text-gray-100">
          {showSubject && (
            <p className="font-semibold">
              <Segments segments={subjectSegments} />
            </p>
          )}
          <div className="whitespace-pre-wrap break-words leading-5">
            <Segments segments={bodySegments} />
          </div>
        </div>
      )}
      {gaps.length > 0 && (
        <InlineAlert variant="warning">
          {gaps
            .map((gap) => `${VARIABLE_LABELS[gap.name]} is empty for ${gap.count} of ${leads.length} lead${leads.length === 1 ? "" : "s"}`)
            .join("; ")}
          . Add a fallback, e.g. {`{{${gaps[0].name}|...}}`}.
        </InlineAlert>
      )}
    </div>
  );
};

export default MessagePreview;
